import React, { useCallback, useMemo } from "react";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import styles from "./org-select.module.scss";
import { useDependencyInjector } from "@whatslly/commons/di";
import type { IOrganizationStore } from "../../../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../../../services/IOrganizationStore";
import { observer } from "mobx-react-lite";
import { AsyncSelect } from "@whatslly/ui-components/v2/BaseElements/Select";
import { useTranslation } from "react-i18next";
import { FiltersStore } from "../../../../services/filtersStore";
import { Svg } from "@whatslly/ui-components/v2/BaseElements/Svg";
import { HeaderButton } from "@whatslly/ui-components/HeaderButton";
import { modalService } from "@whatslly/ui-components/modals";
import { ChangeOrganizationNameModal } from "./modals/ChangeOrganizationNameModal";
import { Link } from "react-router-dom";
import { AuthorizedAction } from "@whatslly/ui-components/AuthorizedAction";
import { ORG_TYPE_OPTIONS } from "@whatslly/commons/constants/organizationTypes";
import { OrgConfigsStore } from "../../../../services/OrgConfigsStore";
import { ORG_IDENTITY_MODAL_CONFIG_KEYS } from "@whatslly/commons/OrgConfigsFeatures";
import type { OrgConfigs } from "@whatslly/types/OrgConfigs";

export const OrgSelectionSelectComponent: React.FC<{
  showClear?: boolean;
  customHandling?: {
    value: { label: string; value: string; description?: string } | undefined;
    onChange: (selectedOption: { label: string; value: string } | undefined) => void;
  };
}> = observer(({ showClear = true, customHandling }) => {
  const organizationStore = useDependencyInjector<IOrganizationStore>(InjectOrganizationStore);
  const filterStore = useDependencyInjector<FiltersStore>(FiltersStore);
  const { t } = useTranslation();
  const searchContainerRef = React.useRef<HTMLDivElement>(null);
  const [value, onChange] = useMemo(() => {
    if (customHandling) return [customHandling.value, customHandling.onChange];
    return [
      organizationStore.selectedOrg.case({
        fulfilled: (org) => org,
        pending: () => undefined,
        rejected: () => undefined,
      }),
      (selectedOption: { label: string; value: string }) => (filterStore.orgId = selectedOption?.value),
    ];
  }, [customHandling, filterStore, organizationStore.selectedOrg.state]);

  return (
    <>
      <div className={styles.superAdminContainer}>
        <div
          className={styles.searchContainer}
          ref={searchContainerRef}
          onClick={() => {
            searchContainerRef.current?.querySelector("input")?.focus();
          }}
        >
          <Svg className={styles.searchIcon} iconName="search-actions" />
          <AsyncSelect
            showClear={showClear}
            hideDropdownIndicator
            menuWidth="500px"
            value={value}
            data-testid="org-select"
            borderColor="none"
            selectSize={"small"}
            controlClassName={styles.selectControl}
            loadOptionsFromServerFunction={organizationStore.searchOrgs}
            placeholder={t("search_orgs.search_orgs")}
            onChange={onChange}
            CustomOptionComponent={(option: { label: string; value: string; description?: string }) => (
              <div data-testid={`org-option-${option.label}`} className={styles.orgOption}>
                <div>{option.label}</div>
                <div className={styles.subLabel}>{option.description}</div>
              </div>
            )}
          />
        </div>
      </div>
    </>
  );
});

export const OrgSelect: React.FC<{
  hideCreateOrg?: boolean;
  hideChangeOrgName?: boolean;
  showClear?: boolean;
}> = observer(({ hideCreateOrg = false, hideChangeOrgName = false, showClear = true }) => {
  const organizationStore = useDependencyInjector<IOrganizationStore>(InjectOrganizationStore);
  const filterStore = useDependencyInjector<FiltersStore>(FiltersStore);
  const orgConfigsStore = useDependencyInjector(OrgConfigsStore);
  const { t } = useTranslation();
  const handleNameChange = useCallback(async () => {
    const selectedOrg = await organizationStore.selectedOrg;
    const orgId = selectedOrg?.value ?? filterStore.orgId;
    if (!orgId) return;

    const keys = [...ORG_IDENTITY_MODAL_CONFIG_KEYS] as (keyof OrgConfigs)[];
    const cfgRes = await orgConfigsStore.loadConfigs(keys, { orgId });
    const initialIdentityFromServer = cfgRes.success && cfgRes.configs ? cfgRes.configs : {};

    const orgTypeValue = selectedOrg?.orgType;
    const orgTypeOption = orgTypeValue ? ORG_TYPE_OPTIONS.find((opt) => opt.value === orgTypeValue) : undefined;
    const modalProps = {
      orgName: selectedOrg?.label || "",
      orgDescription: selectedOrg?.description || "",
      orgType: orgTypeOption,
      initialIdentityFromServer,
    };
    const result = await modalService.openModal(ChangeOrganizationNameModal, modalProps).promise;
    if (result) {
      organizationStore.changeOrgName({
        newName: result.name,
        description: result.description,
        orgType: result.orgType?.value,
        isActive: result.isActive,
        isPOC: result.isPOC,
        POCExpiration: result.POCExpiration,
        org_partner: result.org_partner,
        defaultLanguage: result.defaultLanguage,
        serverlocation: result.serverlocation,
        available_crm: result.available_crm,
      });
    }
  }, [organizationStore, filterStore, orgConfigsStore]);
  return (
    <div className={styles.superAdminAndLogoContainer}>
      <Link to="/home">
        <SvgV2 className={styles.logo} iconName="tuvis" width={40} height={40} />
      </Link>
      <OrgSelectionSelectComponent showClear={showClear} />
      {!hideCreateOrg && (
        <AuthorizedAction mode="hide" adminOnly>
          <HeaderButton tooltipText={t("search_orgs.invite_to_platform")} testId="create-new-org" onClick={() => organizationStore.createNewOrg()}>
            <SvgV2 iconName={"plus"} />
          </HeaderButton>
        </AuthorizedAction>
      )}
      {organizationStore.selectedOrg.state === "fulfilled" && organizationStore.selectedOrg.value?.value && !hideChangeOrgName && (
        <AuthorizedAction mode="hide">
          <HeaderButton
            tooltipText={t("search_orgs.change_organization_name")}
            testId="rename-org"
            onClick={() => handleNameChange()}
            children={<SvgV2 iconName={`pencile`} />}
          />
        </AuthorizedAction>
      )}
    </div>
  );
});
